import { Link } from 'react-router';

/**
 * Eighty Days' privacy policy, at `/privacy`.
 *
 * Routed *outside* both gates (see App.tsx), which is the point of it: a privacy
 * policy is what you show someone before they have an account, and one reachable
 * only after signing in is one the people most entitled to read it cannot.
 *
 * The prose is compiled in rather than stored like the partner agreement in
 * `settings/terms`. Three reasons, in descending order of how much they matter:
 * Article 3 is a table, which the admin's rich-text editor cannot represent and
 * would silently flatten on the first edit; nobody *accepts* this document, so
 * there is no acceptance to keep in step with a version; and it has been revised
 * once since 2018, so "no deploy needed" buys very little. If the editor ever
 * grows tables and headings, this is a reasonable thing to move.
 *
 * Transcribed verbatim, including its own typographical slips — this is a legal
 * document, and tidying its wording is not a frontend change. The one exception
 * is the stray backticks the source used for apostrophes.
 *
 * Standalone rather than inside `PublicLayout`: that layout's header and footer
 * both read Firestore as a signed-in user, and this page is for visitors who are
 * not one.
 */
export function PagePrivacy() {
  return (
    <div className='tw:min-h-screen tw:bg-ed-cream tw:text-ed-black'>
      <header className='tw:border-b tw:border-ed-umber/20 tw:px-6 tw:py-6'>
        <Link to='/' className='tw:mx-auto tw:flex tw:max-w-3xl tw:items-center tw:gap-4'>
          <img src='/eightydays_balloon.png' alt='' className='tw:h-10 tw:w-auto tw:object-contain' />
          <span className='tw:flex tw:flex-col'>
            <span className='tw:font-serif tw:text-xl tw:tracking-wider tw:uppercase'>Eighty Days</span>
            <span className='tw:text-[0.625rem] tw:tracking-[0.2em] tw:uppercase tw:text-ed-umber'>
              Exclusive Experiences
            </span>
          </span>
        </Link>
      </header>

      <main className='tw:mx-auto tw:max-w-5xl tw:px-6 tw:py-16'>
        <h1 className='tw:font-serif tw:text-4xl tw:font-light'>Privacy policy</h1>

        <p className='tw:mt-8 tw:text-ed-charcoal'>
          This privacy policy (“Policy” set out the manner Eighty Days Inc. (Company”) collect and
          handle all information (“User Information”) relating to customers (“User”) who are using
          the travel services provided by the Company (“Services”). The Company shall handle the
          User Information as follows:
        </p>

        <Article title='Article 1 (General Provision)'>
          <Numbered>
            <li>
              In order to protect the User Information, the Company will comply with the Japanese
              Personal Information Protection Law (“Japanese Act”) and other related laws and
              regulations, and endeavor to appropriately handle and protect the User Information,
              including User’s Personal Information.
            </li>
            <li>
              This Policy shall only apply to the use of the Services by User. The Company may
              specify additional provisions for handling of the User Information in other privacy
              policy posted on the website operated by the Company or in the “term of use” relating
              the Services, as such, the applicable provisions shall also be applied, and in the
              event the such provisions conflict with this Policy, this Policy shall prevail.
            </li>
            <li>
              This Policy shall not apply to services provided by a service provider other than the
              Company (“Outside Services”), even if such Outside Services are identified as a
              service in conjunction to the Services (“Affiliate Services”) which being provided by
              the Company’s affiliates (“Affiliates”). For the handling of the User Information in
              Affiliate Services or Outside Services, please refer to the privacy policy separately
              provided by the Affiliates or the provider providing Outside Services.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 2 (Information to be Acquired)'>
          <Numbered>
            <li>
              <p>
                In the Services, the Company shall acquire the User Information that include
                Personal Information (for avoidance of doubt the term “Personal Information” means
                personal information as defined under Article 2, Paragraph 1 of the Japanese Act,
                and the same shall apply hereinafter) as set forth below.
              </p>
              <p className='tw:mt-4'>(1) Information provides by User.</p>
              <p className='tw:mt-2'>
                The Company shall obtain User’s name, address, sex, date of birth and age, passport
                number, e-mail address, telephone number, information required for payment for the
                Services (such as bank account information to be transferred), information of User’s
                identification card, and other information related to User being specified by the
                Company.
              </p>
              <p className='tw:mt-4'>(2) The following information is acquired when using the Services.</p>
              <p className='tw:mt-2'>(i) Device Information</p>
              <p className='tw:mt-2'>
                When a User uses the Services on a device or a mobile terminal, the Company may
                collect device information (such as. ID information that can identify the terminal,
                etc.) used by the User to maintain and improve the Services or to prevent any fraud
                activities.
              </p>
              <p className='tw:mt-4'>
                (ii) Log-In Information, Access History Information, and the Services Usage
                Information
              </p>
              <p className='tw:mt-2'>
                To maintain and improve the Services or prevent fraud activity, the Company may
                collect the IP addresses that are automatically generated and stored when using the
                Services, the date and time of User’s request, the usage history of the Services and
                the status of User.
              </p>
              <p className='tw:mt-4'>(iii) Cookie and Anonymized ID</p>
              <p className='tw:mt-2'>
                The Services use a technology called “Cookie” or other similar technology. Cookie is
                an industry-standard technique by which web servers able to identify User’s device.
                The Cookie can only identify User’s device but cannot identify User individuals. The
                function of the Cookie can be disabled by changing the settings on the device,
                however by doing so, part of or all the Services may not be available.
              </p>
              <p className='tw:mt-4'>
                (iv) Information to be acquired from using the Outside Services or Affiliate Services
              </p>
              <p className='tw:mt-2'>
                Information, including ID, that User have given consent to be shared when using
                Outside Services or Affiliate Services, in accordance with the privacy policy provide
                by the provider of Outside Services or Affiliates.
              </p>
            </li>
            <li>
              When acquiring Personal Information, Company shall properly acquire such personal
              information through no falsification or other improper means. In addition, in the event
              Company collect any User Information from sources other than from using the Services,
              Company will publicly announce or notify User, in advance regarding the purpose of its
              use.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 3 (Purpose of Use)'>
          <Numbered>
            <li>
              The Company will appropriately handle the User Information obtained through the use of
              the Services within the extent of the purpose of use set forth in the table below.
              Without User’s consent, the Company shall never use the User Information for any other
              purpose beyond the scope of the purpose of use therein.
            </li>
          </Numbered>

          <PurposeTable />

          <Numbered start={2} className='tw:mt-8'>
            <li>
              The Company may revise the purpose of use set forth in the preceding clause, to the
              extent that such revision is deemed to be reasonably related to the original purpose of
              use, upon which, the Company will either notify User or make announcement on the
              Company’s website or through other means that can be easily accessed and viewed.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 4 (Provision to Third Parties)'>
          <Numbered>
            <li>
              <p>
                In principle, the Company will not provide User’s Personal Information to any third
                parties without the consent of User. However, that in the event of any of the
                following, Personal Information may be provided to a third party without the consent
                of User, to the extent that such provision does not conflict with any applicable laws
                and regulations.
              </p>
              <p className='tw:mt-2'>(1) Based on laws and regulations;</p>
              <p className='tw:mt-2'>
                (2) When it is necessary to protect the life, body or property of an individual and
                User’s consent is difficult to obtain;
              </p>
              <p className='tw:mt-2'>
                (3) When it is necessary for improving public health or ensuring the well-being of
                children and User’s consent is difficult to obtain;
              </p>
              <p className='tw:mt-2'>
                (4) When it is necessary for cooperating with government agencies, local public
                bodies, or any agents thereof, in executing the affairs prescribed by laws and
                regulations, and obtaining User’s consent is likely to impede the execution of the
                affairs concerned; or
              </p>
              <p className='tw:mt-2'>
                (5) When the Personal Information is succeeded due to the Company being merged,
                spilt, transferred or any other similar business transaction.
              </p>
            </li>
            <li>
              Notwithstanding the preceding paragraph, the Company shall be entitled to provide
              Affiliates or other third party, in order to facilitate User’s reservation of
              accommodation or transportation made by User through the Services with Affiliate
              Services or Outside Services.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 5 (Outsourcing)'>
          <p>
            The Company may outsource the handling or management of Personal Information, in whole or
            in part, to one or more independent services provider company in order to achieve the
            purpose of use. Upon which the Company will in advance execute a confidentiality
            agreement with each of the respective independent services provider company, of the terms
            and conditions that conform to the provisions of this Policy and conduct necessary and
            appropriate supervision to ensure proper and safe management of the Personal Information.
          </p>
        </Article>

        <Article title='Article 6 (Sharing)'>
          <p>
            The Company may share the Personal Information obtained from User with Affiliates or any
            third party, to the extent necessary for the provision of tie-up services of the Services
            through the Affiliate Services or Outside Services. In this circumstance, the Company
            will publicize in advance; (i) the itemization of the information to be shared, (ii) the
            scope of the use, (iii) the purpose of use, and (iv) the name of the person responsible
            for the management of such information.
          </p>
        </Article>

        <Article title='Article 7 (Information Collection Module)'>
          <p>
            The Services may incorporate “information-gathering module” to analyze information such
            as the status of the Services’ use and advertising effects related to the Services.
          </p>
          <p className='tw:mt-4'>
            Accordingly, the Company may provide the User Information to the provider of the
            “information-gathering module”. The “information collection module” collects the User
            Information that does not contain personally identifiable information, and the
            information collected is managed in accordance with the privacy policy or the like of the
            provider of the “information collection module”. The following are the “information
            collection module” that the Company incorporate:
          </p>
          <div className='tw:mt-4 tw:border-l-2 tw:border-ed-gold/50 tw:pl-4'>
            <p>Name of the Information Collection Module: Google Analytics</p>
            <p>Provider: Google Inc.</p>
            <p>
              Privacy Policy:{' '}
              <a
                href='http://www.google.com/intl/ja/policies/privacy/'
                target='_blank'
                rel='noopener noreferrer'
                className='tw:break-all tw:text-ed-umber tw:underline tw:underline-offset-2 tw:hover:text-ed-gold'
              >
                http://www.google.com/intl/ja/policies/privacy/
              </a>
            </p>
          </div>
        </Article>

        <Article title='Article 8 (Behavioral Targeting Advertisement)'>
          <Numbered>
            <li>
              <p>
                The Company or a third party may implement a targeting advertisement based on the
                User Information as part of the Service, as such the following information may be
                gathered and stored by us or a third party by using Information Gathering Modules as
                provided in preceding article.
              </p>
              <p className='tw:mt-2'>
                (1) Information on action history and service usage status (this information is used
                for analysis of User’s needs, interests etc. which does not identify User
                specifically);
              </p>
              <p className='tw:mt-2'>(2) Device information; and</p>
              <p className='tw:mt-2'>(3) Positioning Information.</p>
            </li>
            <li>
              The Company may distribute the information gathered prescribed in Article 8.1 to
              company that doing business in behavioral targeting advertisement (“Advertisement
              Distributor Operator”) for the purpose in delivering optimal customized advertisements
              to User in accordance with User’s needs, interests etc.
            </li>
            <li>
              The handling the information by the Advertisement Distributor Operator as defined in
              the preceding paragraph, shall be in accordance with the privacy policy and other
              conditions prescribed by such Advertisement Distribution Operator
            </li>
          </Numbered>
        </Article>

        <Article title='Article 9 (Security Management System)'>
          <Numbered>
            <li>
              For the purpose of preventing any leakage, loss or damage of the User Information, the
              Company will necessary and appropriate measures to securely manage the User Information
              by allowing only minimal and limited access to files containing Personal Information,
              maintaining access logs, and installing security software in order to prevent outside
              unauthorized access.
            </li>
            <li>
              The Company appoints the representative director as the person in charge to properly
              manage the User Information and to continuously make improvements in the quality of
              such management.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 10 (Revision)'>
          <Numbered>
            <li>
              The Company shall review the operation of the User Information from time to time and
              will make continuous improvements, as such, the Company may revise this Policy from
              time to time as necessary.
            </li>
            <li>
              The Company will notify User of any revisions to this Policy by announcing such
              revisions on the Company’s website or through other means that can be easily accessed
              and viewed. However, if the Company is required by applicable laws or regulations to
              obtain User’s consent in order for the Company to make certain revisions, then the
              Company will obtain User’s consent in a manner separately established the Company.
            </li>
          </Numbered>
        </Article>

        <Article title='Article 11 (Inquiry)'>
          <p>
            For any inquiries regarding the Company’s handling of the User Information, including any
            suggestions, questions, complaints, request or comments, please contact the Company at
            the following detail. However, the Company may ignore any inquires made by User, in the
            event that the Company does not assume these obligations under the Japanese Act or other
            laws and regulations, or in the event that requests by User have been made repeatedly
            many times without due reason, or in the event that excessive technical work is required.
          </p>
          {/* The address and number the policy itself names — deliberately not
              `config/contact`, which is where partners are told to write about
              bookings. This is the data-protection desk. */}
          <div className='tw:mt-4 tw:border-l-2 tw:border-ed-gold/50 tw:pl-4'>
            <p>Eighty Days Inc.</p>
            <p>
              Tel:{' '}
              <a href='tel:+81367308485' className='tw:hover:text-ed-gold'>
                +81 3 6730 8485
              </a>
            </p>
            <p>
              Email:{' '}
              <a
                href='mailto:contact@80days.co.jp'
                className='tw:text-ed-umber tw:underline tw:underline-offset-2 tw:hover:text-ed-gold'
              >
                contact@80days.co.jp
              </a>
            </p>
            <p>The reception time shall be from 10:00 to 17:00 on weekdays.</p>
          </div>
        </Article>

        <p className='tw:mt-16 tw:border-t tw:border-ed-umber/20 tw:pt-8 tw:text-sm tw:text-ed-stone'>
          Established on 2018/11/26
        </p>
      </main>
    </div>
  );
}

/** One numbered article: its heading and whatever the article is made of. */
function Article({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className='tw:mt-12 tw:text-ed-charcoal'>
      <h2 className='tw:mb-4 tw:font-serif tw:text-xl tw:font-light tw:text-ed-black'>{title}</h2>
      {children}
    </section>
  );
}

/**
 * The clause list an article is written as. Tailwind's preflight strips list
 * styling, so the numbering and indent are restored here; `start` is what lets
 * Article 3 resume at 2 after the table interrupts it.
 */
function Numbered({
  children,
  start,
  className,
}: {
  children: React.ReactNode;
  start?: number;
  className?: string;
}) {
  return (
    <ol start={start} className={`tw:list-decimal tw:space-y-4 tw:pl-6 ${className ?? ''}`}>
      {children}
    </ol>
  );
}

/** Stacked paragraphs inside a table cell — every cell of the table holds several. */
function Cell({ lines }: { lines: string[] }) {
  return (
    <td className='tw:border tw:border-ed-umber/20 tw:p-3 tw:align-top'>
      {lines.map((line, i) => <p key={i} className={i === 0 ? '' : 'tw:mt-3'}>{line}</p>)}
    </td>
  );
}

/**
 * Article 3's table of processing objectives, and the reason this document is a
 * page rather than a settings field: there is nothing else on the site that has
 * to render one, and the admin's rich-text editor cannot produce it.
 *
 * Scrolls sideways inside its own box rather than squeezing three columns of
 * prose onto a phone.
 */
function PurposeTable() {
  return (
    <div className='tw:mt-6 tw:overflow-x-auto'>
      <table className='tw:w-full tw:min-w-[48rem] tw:border-collapse tw:text-sm'>
        <caption className='tw:mb-2 tw:text-left tw:text-sm tw:font-medium tw:text-ed-black'>Table</caption>
        <thead>
          <tr className='tw:bg-ed-umber/10 tw:text-left'>
            <th className='tw:border tw:border-ed-umber/20 tw:p-3 tw:font-medium'>Processing Objective</th>
            <th className='tw:border tw:border-ed-umber/20 tw:p-3 tw:font-medium'>Processing Purpose</th>
            <th className='tw:border tw:border-ed-umber/20 tw:p-3 tw:font-medium'>Information</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <Cell lines={['Provision, maintenance and improvement of the Services']} />
            <Cell
              lines={[
                'To reserve accommodations made through the Services and to confirm the identity of individual using the accommodation (including the provision of information to the entity providing the accommodation facilities), and to prevent unauthorized processing of accommodation reservation service.',
                'To make reservations of transportation made through the Services and to confirmation the identity of individual using the transportation (including the provision of information to the companies providing the means of transportation), and to prevent authorized processing of the transportation reservation service.',
                'For the smooth provision, maintenance and improvement of the Services.',
                'For the settlement of accommodation and transportation expenses, etc.',
                'For the purpose of customizing information for the circulation of advertisements according to the age, occupation, sex, hobbies, etc. of User on the Services.',
                'To improve quality of the Services, such as by improving convenience and functionality.',
                'For temporary storage backup for the purpose of the operation of the Services.',
                'For conducting surveys on the processing of situation and environment and for producing statistical date for the purpose of improving the Services’ website.',
                'To protect the Company and User from any security risk and malicious system',
              ]}
            />
            <Cell
              lines={[
                'Name',
                'Address',
                'Sex',
                'Date of Birth and Age',
                'Passport Number',
                'E-mail Address',
                'Telephone Number',
                'User’s information that is necessary for payment for the Services (such as, Bank Account information)',
                'Log-in and Browsing History Information',
                'Information related to Individual Identity Certificate',
              ]}
            />
          </tr>
          <tr>
            <Cell lines={['Notification and Response to User']} />
            <Cell
              lines={[
                'For the purpose of responding to inquiries about the Services.',
                'To response to any violation of the “Terms and Conditions” for the processing of the Services and for prevention of such violation.',
                'For the purpose of notifying important announcement of the Service, including but not limited to, amendment of the “Terms and Conditions” for the processing of the Services or this Policy, or suspending, discontinuing, or canceling the Services.',
                'For dealing with disputes, lawsuits, etc.',
              ]}
            />
            <Cell
              lines={[
                'Contact Information, such as E-Mail Address',
                'Name, Affiliation and other Information necessary for the provision of the Services.',
              ]}
            />
          </tr>
          <tr>
            <Cell lines={['Outsourcing the Handling of Personal Information to a Third Party']} />
            <Cell
              lines={[
                'The Third Party receiving the information listed on the right column, shall process such information for the following purposes:',
                'To analyses such information, in order to optimize the circulation of advertisement in collaboration with the advertisement platforms and DSP providers; and to provide collaborative services',
                'To support the Company’s marketing operations.',
              ]}
            />
            <Cell lines={['Name, Affiliation and other Information necessary for the provision of the Services.']} />
          </tr>
          <tr>
            <Cell lines={['Creation of Statistical Data']} />
            <Cell
              lines={[
                'For the smooth provision, maintenance and improvement of the Services',
                'For the purpose of customizing information for the circulation of advertisements according to the age, occupation, sex, hobbies, etc. of User.',
                'To improve the Services quality, such as by improving convenience and functionality.',
              ]}
            />
            <Cell
              lines={[
                'All or part of the information listed above.',
                'E-Mail Address',
                'Name, Affiliation and other Information necessary for the provision of the Services.',
                'User’s information that is necessary for payment for the Services (such as, Bank Account information)',
                'Log-in and Browsing History Information',
              ]}
            />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
